import { UserSettingsType } from "~/types-and-enums/settingsTypes";

export const DD_MM_YYYY = "dd.MM.yyyy";
export const YYYY_MM_DD = "yyyy-MM-dd";
export const HH_MM = "HH:mm";
export const DB_DATE_FORMAT = "yyyy-MM-dd";
export const DN_DD_MM_YYYY_HH_MM = "dd.MM.yyyy HH:mm";
export const DD_MM = "dd.MM";
export const NOT_APPLICABLE = "N/A";
export const CLIENT_API_PREFIX = "client-api";

const rawSupplierHost = process.env.NEXT_PUBLIC_SUPPLIER_APP_SERVICE_URL;
const nodeEnv = process.env.NODE_ENV;

const sanitizeHost = (host: string) => host.replace(/\/+$/, "");

const computeApiHost = (isServer: boolean) => {
  if (isServer) {
    // Use full backend URL for server-side
    if (!rawSupplierHost) {
      if (nodeEnv === "test") return "http://localhost";
      return "";
    }
    const protocol = rawSupplierHost.startsWith("http")
      ? ""
      : process.env.NEXT_PUBLIC_SUPPLIER_APP_SERVICE_PROTOCOL || "http";
    return protocol ? `${protocol}://${sanitizeHost(rawSupplierHost)}` : sanitizeHost(rawSupplierHost);
  } else {
    // Client-side: use /api so rewrites work
    return `/${CLIENT_API_PREFIX}`;
  }
};

export const API_HOST = typeof window === "undefined" ? computeApiHost(true) : computeApiHost(false);

export const EMAIL_SERVICE = "service@andercore.com";
export const EMAIL_SALES = "sales@andercore.com";
export const EMAIL_PROCUREMENT = "procurement@andercore.com";
export const EMAIL_LOGISTICS = "logistics@andercore.com";
export const CONTACT_NUMBER = "+49 30 30809217";
export const EMPTY_DATE = "--.--.--";

export const DEFAULT_PAGE_SIZE = 20;
export const TILDE = "~";

export const BULLET_POINT = " • ";

export const USER_SETTINGS_EMPTY: UserSettingsType = {
  company_information: {
    name: "",
    address: {
      street: "",
      postal_code: "",
      city: null,
      country: null,
    },
    credit_limit: {
      value: 0,
      currency: "",
    },
    store_access: false,
  },
  user_data: {
    first_name: "",
    last_name: "",
    email: "",
  },
  account_manager: {
    name: "",
    telephone: null,
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    profile_photo_url: "",
  },
  preferences: {
    locale: null,
    notification: {
      delivery_updates: {
        email: {
          enabled: false,
          recipient: "",
        },
        sms: {
          enabled: false,
          recipient: null,
        },
        whatsapp: {
          enabled: false,
          recipient: null,
        },
      },
      newsletter: {
        email: {
          enabled: false,
          recipient: null,
        },
      },
      storeLaunch: false,
    },
  },
  delivery_address: {
    street: "",
    postal_code: "",
    city: "",
    country: "",
    email: "",
    delivery_contact_name: "",
    delivery_contact_email: "",
    delivery_contact_phone: "",
    main_warehouse_opening_times: "",
    maximum_delivery_capacity: "",
    accept_container_delivery: false,
    forklift_available: false,
    delivery_additional_info: "",
    company_name: "",
    is_address_validated: false,
  },
  delivery_contact: {
    name: "",
    phone: "",
    email: "",
  },
  delivery_info: {
    delivery_times: "",
    maximum_delivery_capacity: "",
    accept_container_delivery: "",
    forklift_available: "",
    delivery_additional_info: "",
  },
  billing_address: {
    street: "",
    postal_code: "",
    city: "",
    country: "",
    email: "",
  },
};

export const COUNTRY_PHONE_LIST = [
  {
    id: 1,
    value: "+49",
    label: "Germany",
    flagUrl: "DE",
    code: "+49",
  },
  {
    id: 2,
    value: "+33",
    label: "France",
    flagUrl: "FR",
    code: "+33",
  },
  {
    id: 3,
    value: "+34",
    label: "Spain",
    flagUrl: "ES",
    code: "+34",
  },
  {
    id: 4,
    value: "+44",
    label: "United Kingdom",
    flagUrl: "GB",
    code: "+44",
  },
  {
    id: 5,
    value: "+32",
    label: "Belgium",
    flagUrl: "BE",
    code: "+32",
  },
  {
    id: 6,
    value: "+31",
    label: "Netherlands",
    flagUrl: "NL",
    code: "+31",
  },
  {
    id: 7,
    value: "+351",
    label: "Portugal",
    flagUrl: "PT",
    code: "+351",
  },
  {
    id: 8,
    value: "+43",
    label: "Austria",
    flagUrl: "AT",
    code: "+43",
  },
  {
    id: 9,
    value: "+48",
    label: "Poland",
    flagUrl: "PL",
    code: "+48",
  },
  {
    id: 10,
    value: "+41",
    label: "Switzerland",
    flagUrl: "CH",
    code: "+41",
  },
  {
    id: 11,
    value: "+420",
    label: "Czech Republic",
    flagUrl: "CZ",
    code: "+420",
  },
  {
    id: 12,
    value: "+39",
    label: "Italy",
    flagUrl: "IT",
    code: "+39",
  },
  {
    id: 13,
    value: "+30",
    label: "Greece",
    flagUrl: "GR",
    code: "+30",
  },
  {
    id: 14,
    value: "+46",
    label: "Sweden",
    flagUrl: "SE",
    code: "+46",
  },
  {
    id: 15,
    value: "+45",
    label: "Denmark",
    flagUrl: "DK",
    code: "+45",
  },
  {
    id: 16,
    value: "+47",
    label: "Norway",
    flagUrl: "NO",
    code: "+47",
  },
  {
    id: 17,
    value: "+358",
    label: "Finland",
    flagUrl: "FI",
    code: "+358",
  },
  {
    id: 18,
    value: "+353",
    label: "Ireland",
    flagUrl: "IE",
    code: "+353",
  },
  {
    id: 19,
    value: "+36",
    label: "Hungary",
    flagUrl: "HU",
    code: "+36",
  },
  {
    id: 20,
    value: "+40",
    label: "Romania",
    flagUrl: "RO",
    code: "+40",
  },
  {
    id: 21,
    value: "+359",
    label: "Bulgaria",
    flagUrl: "BG",
    code: "+359",
  },
  {
    id: 22,
    value: "+385",
    label: "Croatia",
    flagUrl: "HR",
    code: "+385",
  },
  {
    id: 23,
    value: "+386",
    label: "Slovenia",
    flagUrl: "SI",
    code: "+386",
  },
  {
    id: 24,
    value: "+421",
    label: "Slovakia",
    flagUrl: "SK",
    code: "+421",
  },
  {
    id: 25,
    value: "+370",
    label: "Lithuania",
    flagUrl: "LT",
    code: "+370",
  },
  {
    id: 26,
    value: "+371",
    label: "Latvia",
    flagUrl: "LV",
    code: "+371",
  },
  {
    id: 27,
    value: "+372",
    label: "Estonia",
    flagUrl: "EE",
    code: "+372",
  },
  {
    id: 28,
    value: "+352",
    label: "Luxembourg",
    flagUrl: "LU",
    code: "+352",
  },
  {
    id: 29,
    value: "+356",
    label: "Malta",
    flagUrl: "MT",
    code: "+356",
  },
  {
    id: 30,
    value: "+357",
    label: "Cyprus",
    flagUrl: "CY",
    code: "+357",
  },
  {
    id: 31,
    value: "+354",
    label: "Iceland",
    flagUrl: "IS",
    code: "+354",
  },
  {
    id: 32,
    value: "+423",
    label: "Liechtenstein",
    flagUrl: "LI",
    code: "+423",
  },
  {
    id: 33,
    value: "+377",
    label: "Monaco",
    flagUrl: "MC",
    code: "+377",
  },
  {
    id: 34,
    value: "+378",
    label: "San Marino",
    flagUrl: "SM",
    code: "+378",
  },
  {
    id: 35,
    value: "+39",
    label: "Vatican City",
    flagUrl: "VA",
    code: "+39",
  },
  {
    id: 36,
    value: "+382",
    label: "Montenegro",
    flagUrl: "ME",
    code: "+382",
  },
  {
    id: 37,
    value: "+383",
    label: "Kosovo",
    flagUrl: "XK",
    code: "+383",
  },
  {
    id: 38,
    value: "+389",
    label: "North Macedonia",
    flagUrl: "MK",
    code: "+389",
  },
  {
    id: 39,
    value: "+387",
    label: "Bosnia and Herzegovina",
    flagUrl: "BA",
    code: "+387",
  },
  {
    id: 40,
    value: "+381",
    label: "Serbia",
    flagUrl: "RS",
    code: "+381",
  },
  {
    id: 41,
    value: "+355",
    label: "Albania",
    flagUrl: "AL",
    code: "+355",
  },
  {
    id: 42,
    value: "+90",
    label: "Turkey",
    flagUrl: "TR",
    code: "+90",
  },
  {
    id: 43,
    value: "+380",
    label: "Ukraine",
    flagUrl: "UA",
    code: "+380",
  },
  {
    id: 44,
    value: "+375",
    label: "Belarus",
    flagUrl: "BY",
    code: "+375",
  },
  {
    id: 45,
    value: "+7",
    label: "Russia",
    flagUrl: "RU",
    code: "+7",
  },
  {
    id: 46,
    value: "+373",
    label: "Moldova",
    flagUrl: "MD",
    code: "+373",
  },
  {
    id: 47,
    value: "+376",
    label: "Andorra",
    flagUrl: "AD",
    code: "+376",
  },
];

export const COUNTRY_NAMES_LIST = [
  {
    id: 1,
    value: "Germany",
    label: "Germany",
  },
  {
    id: 2,
    value: "France",
    label: "France",
  },
  {
    id: 3,
    value: "Spain",
    label: "Spain",
  },
  {
    id: 4,
    value: "United Kingdom",
    label: "United Kingdom",
  },
  {
    id: 5,
    value: "Belgium",
    label: "Belgium",
  },
  {
    id: 6,
    value: "Netherlands",
    label: "Netherlands",
  },
  {
    id: 7,
    value: "Portugal",
    label: "Portugal",
  },
  {
    id: 8,
    value: "Austria",
    label: "Austria",
  },
  {
    id: 9,
    value: "Poland",
    label: "Poland",
  },
  {
    id: 10,
    value: "Switzerland",
    label: "Switzerland",
  },
  {
    id: 11,
    value: "Czech Republic",
    label: "Czech Republic",
  },
  {
    id: 12,
    value: "Italy",
    label: "Italy",
  },
  {
    id: 13,
    value: "Greece",
    label: "Greece",
  },
  {
    id: 14,
    value: "Sweden",
    label: "Sweden",
  },
  {
    id: 15,
    value: "Denmark",
    label: "Denmark",
  },
  {
    id: 16,
    value: "Norway",
    label: "Norway",
  },
  {
    id: 17,
    value: "Finland",
    label: "Finland",
  },
  {
    id: 18,
    value: "Ireland",
    label: "Ireland",
  },
  {
    id: 19,
    value: "Hungary",
    label: "Hungary",
  },
  {
    id: 20,
    value: "Romania",
    label: "Romania",
  },
  {
    id: 21,
    value: "Bulgaria",
    label: "Bulgaria",
  },
  {
    id: 22,
    value: "Croatia",
    label: "Croatia",
  },
  {
    id: 23,
    value: "Slovenia",
    label: "Slovenia",
  },
  {
    id: 24,
    value: "Slovakia",
    label: "Slovakia",
  },
  {
    id: 25,
    value: "Lithuania",
    label: "Lithuania",
  },
  {
    id: 26,
    value: "Latvia",
    label: "Latvia",
  },
  {
    id: 27,
    value: "Estonia",
    label: "Estonia",
  },
  {
    id: 28,
    value: "Luxembourg",
    label: "Luxembourg",
  },
  {
    id: 29,
    value: "Malta",
    label: "Malta",
  },
  {
    id: 30,
    value: "Cyprus",
    label: "Cyprus",
  },
  {
    id: 31,
    value: "Iceland",
    label: "Iceland",
  },
  {
    id: 32,
    value: "Liechtenstein",
    label: "Liechtenstein",
  },
  {
    id: 33,
    value: "Monaco",
    label: "Monaco",
  },
  {
    id: 34,
    value: "San Marino",
    label: "San Marino",
  },
  {
    id: 35,
    value: "Vatican City",
    label: "Vatican City",
  },
  {
    id: 36,
    value: "Montenegro",
    label: "Montenegro",
  },
  {
    id: 37,
    value: "Kosovo",
    label: "Kosovo",
  },
  {
    id: 38,
    value: "North Macedonia",
    label: "North Macedonia",
  },
  {
    id: 39,
    value: "Bosnia and Herzegovina",
    label: "Bosnia and Herzegovina",
  },
  {
    id: 40,
    value: "Serbia",
    label: "Serbia",
  },
  {
    id: 41,
    value: "Albania",
    label: "Albania",
  },
  {
    id: 42,
    value: "Turkey",
    label: "Turkey",
  },
  {
    id: 43,
    value: "Ukraine",
    label: "Ukraine",
  },
  {
    id: 44,
    value: "Belarus",
    label: "Belarus",
  },
  {
    id: 45,
    value: "Russia",
    label: "Russia",
  },
  {
    id: 46,
    value: "Moldova",
    label: "Moldova",
  },
  {
    id: 47,
    value: "Andorra",
    label: "Andorra",
  },
];

export const SEARCH_PARAM_SET_PASSWORD_MODAL = "set-password-modal";
export const SEARCH_PARAM_UPDATE_PASSWORD_MODAL = "update-password-modal";
export const SEARCH_PARAM_RESET_PASSWORD_MODAL = "reset-password-modal";
export const SEARCH_PARAM_RESOLVE_CLAIM_MODAL = "resolve-claim-modal";
export const SEARCH_PARAM_REGISTRATION_MODAL = "registration-modal";
export const SEARCH_PARAM_REGISTRATION_REQUEST_SUBMITTED_MODAL = "request-submitted-modal";
export const SEARCH_PARAM_PQ_INGESTION_FILE_UPLOAD_MODAL = "pq-ingestion-file-upload-modal";
export const SEARCH_PARAM_CREATE_SINGLE_OFFER_MODAL = "create-single-offer-modal";
export const SEARCH_PARAM_UPDATE_SINGLE_OFFER_MODAL = "update-single-offer-modal";
export const SETTINGS_PREFERENCES_UPDATE_MODAL = "user-preferences-update-modal";

// List of ISO 3166-1 alpha-2 country codes
export const COUNTRY_CODES = [
  "AF",
  "AL",
  "DZ",
  "DE",
  "AU",
  "AT",
  "BE",
  "BR",
  "CA",
  "CN",
  "FR",
  "GB",
  "IN",
  "IT",
  "JP",
  "MX",
  "NL",
  "NZ",
  "RU",
  "ES",
  "US",
  "ZA",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "GR",
  "HU",
  "IE",
  "LV",
  "LT",
  "LU",
  "MT",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "SE",
  "NO",
  "CH",
  "TR",
  // Add more country codes as necessary
];

export const EMPTY_PRICE_PLACEHOLDER = "-,-- €";

export const LOCALES = [{ locale: "en" }, { locale: "de" }];

export const sarahBlancImageUrl = "/images/sarahBlanc.jpeg";
export const ELSACORDONNIER_EMAIL = "elsa.cordonnier@andercore.com";
export const ELSA = "Elsa";
export const CORDONNIER = "Cordonnier";
export const SARAH = "Sarah";
export const BLANC = "Blanc";
export const SARAHBLANC_EMAIL = "sarah.blanc@andercore.com";
export const OTP_EXPIRY_TIMESTAMP = "otp-expiry-timestamp";

export const FILE_TYPE_INVOICE = "Invoice";
export const FILE_TYPE_FLASHLIST = "Flashlist";
